import { useNavigate } from "react-router-dom"
import { makeStyles } from "tss-react/mui"
import { MERCHANT_ADMIN_EXTRA_SERVICES_CREATE_ROUTE, MERCHANT_ADMIN_ROUTE } from "../../../../../constants/routes"
import { useAppLocalization } from "../../../../localization/components/AppLocalizationProvider"
import { PageHeader } from "../../../../../shared/components/pageHeader/PageHeader"
import { LocalizedStrict } from "../../../../localization/components/AppLocalized"
import { Page } from "../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../shared/components/links/StandardBreadcrumbs"
import { StandardButton } from "../../../../../shared/components/buttons/StandardButton"
import { COLUMN_SIZE } from "../../../../../shared/constants/table/config"
import { Grid } from "@mui/material"
import { StandardTwoLinesCell } from "../../../../../shared/components/table/StandardTwoLinesCell"
import { useMemo, useState } from "react"
import { MRT_ColumnDef } from "material-react-table"
import { useApiQueryTable } from "../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../core/api/useApiQuery"
import { useMerchant } from "../../../../merchants/merchantHooks"
import { ExtraServicesQuery, fetchExtraServices, FetchExtraServicesResponse } from "../extraServicesApi"
import { ExternalServiceEntry, ExtraServicePricingType } from "../extraServicesTypes"
import {
    defaultConfirmModalState,
    DefaultConfirmModalStateType,
    DeleteExtraServiceDialog,
} from "../components/DeleteExtraServiceDialog"
import { renderRowActionMenuItems } from "../components/renderRowActionMenuItems"
import { useMoney } from "../../../../../modules/currency/useMoney"
import { toDineroOrUndefined } from "../../../../../modules/currency/currencyLib"

const useStyles = makeStyles()(() => ({
    headCell: {
        "& .Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
}))

const transformProductsData = (data: FetchExtraServicesResponse) => data.extraServices

export function ExtraServices() {
    const { classes } = useStyles()
    const { l10n } = useAppLocalization()
    const navigate = useNavigate()
    const { merchantId } = useMerchant()
    const moneyFactory = useMoney({})

    const [confirmModalState, setConfirmModalState] = useState<DefaultConfirmModalStateType>(defaultConfirmModalState)

    const services = useApiQuery(fetchExtraServices, {
        queryName: ExtraServicesQuery.LIST,
        dependencies: {
            merchantId: merchantId,
        },
        select: transformProductsData,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(services)

    const columns = useMemo<MRT_ColumnDef<ExternalServiceEntry>[]>(
        () => [
            columnFactory.nameColumn({
                accessorKey: "name",
                size: COLUMN_SIZE.XL,
                header: l10n.getString("extra-services-services-column"),
                Cell: ({ row }) => {
                    const entry = row.original

                    if (entry.pricingType === ExtraServicePricingType.FLAT_FEE) {
                        const amount = toDineroOrUndefined(entry.price)
                        const subtitle1 = amount
                            ? `${l10n.getString("extra-services-price-subline-fixed")}: ${moneyFactory.format(amount)}`
                            : ""

                        return <StandardTwoLinesCell title={entry.name} subtitle1={subtitle1} />
                    }

                    const perSeat = toDineroOrUndefined(entry.pricePerSeat)
                    const min = toDineroOrUndefined(entry.minimumPrice)
                    const max = toDineroOrUndefined(entry.maximumPrice)

                    const subtitle1 = perSeat
                        ? `${l10n.getString("extra-services-price-subline-per-seat")}: ${moneyFactory.format(perSeat)}`
                        : ""
                    const subtitle2 = min
                        ? `${l10n.getString("extra-services-price-subline-min")}: ${moneyFactory.format(min)}`
                        : null
                    const subtitle3 = max
                        ? `${l10n.getString("extra-services-price-subline-max")}: ${moneyFactory.format(max)}`
                        : null

                    return (
                        <StandardTwoLinesCell
                            title={entry.name}
                            subtitle1={subtitle1}
                            subtitle2={subtitle2}
                            subtitle3={subtitle3}
                        />
                    )
                },
            }),
            columnFactory.typeColumn({
                id: "serviceType",
                header: l10n.getString("extra-services-service-type-column"),
                size: COLUMN_SIZE.M,
                maxSize: COLUMN_SIZE.M,
                accessorFn: (row) =>
                    row.serviceType
                        ? l10n.getStringForEnum(
                              "extra-services-create-form-type-dropdown-item",
                              row.serviceType,
                              row.serviceType,
                          )
                        : "",
            }),
            columnFactory.typeColumn({
                id: "pricingType",
                header: l10n.getString("extra-services-price-type-column"),
                size: COLUMN_SIZE.M,
                maxSize: COLUMN_SIZE.M,
                accessorFn: (row) =>
                    row.pricingType
                        ? l10n.getStringForEnum(
                              "extra-services-create-form-pricing-type-dropdown-item",
                              row.pricingType,
                              row.pricingType,
                          )
                        : "",
            }),
            columnFactory.activeInactiveStatusColumn(
                {
                    id: "status",
                    header: l10n.getString("extra-services-status-column"),
                },
                (row) => row.isActive,
            ),
        ],
        [l10n, columnFactory, moneyFactory],
    )

    return (
        <>
            <LocalizedStrict id="extra-services-page-header" attrs={{ title: true }}>
                <PageHeader
                    title="Extra Services"
                    page={Page.MERCHANT_EXTRA_SERVICES}
                    rightElement={
                        <LocalizedStrict id="extra-services-create-service-button">
                            <StandardButton
                                onClick={() => {
                                    navigate(MERCHANT_ADMIN_EXTRA_SERVICES_CREATE_ROUTE)
                                }}
                            >
                                Create extra service
                            </StandardButton>
                        </LocalizedStrict>
                    }
                >
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="merchant-nav-item-home">
                            <BreadcrumbLink to={MERCHANT_ADMIN_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-products-header">
                            <Breadcrumb>Products</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="merchant-nav-item-extra-services">
                            <Breadcrumb>Extra services</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Grid container direction="column">
                <ApiQueryTable
                    columns={columns}
                    initialState={{ density: "spacious" }}
                    muiTableHeadCellProps={{ className: classes.headCell }}
                    renderRowActionMenuItems={({ row, closeMenu }) =>
                        renderRowActionMenuItems({ row, closeMenu, setConfirmModalState, navigate })
                    }
                />

                {/* Confirm delete */}
                {confirmModalState.showConfirmModal && (
                    <DeleteExtraServiceDialog
                        confirmModalState={confirmModalState}
                        setConfirmModalState={setConfirmModalState}
                    />
                )}
            </Grid>
        </>
    )
}
